import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { ListJobsService } from "@application/useCases/job/ListJobTasksService";
import {
  getSelectedState,
  getSelectedStateFromKeyDown,
  Grid,
  GridColumn,
  GRID_PREVENT_SELECTION_ELEMENT,
  GridCustomCellProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridKeyDownEvent,
  GridRowClickEvent,
  GridSelectionChangeEvent,
} from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { useInjection } from "@presentation/components/dependencyInjection";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useJobListStore } from "@presentation/stores";
import { JobListItemState } from "@presentation/common/types/extensions/JobListItemState";
import { process, DataResult, CompositeFilterDescriptor, FilterDescriptor } from "@progress/kendo-data-query";
import { DropdownFilterCell } from "@presentation/components/DropdownFilterCell";
import { StatusEnumSchema, JobPipelineTypeSchema } from "@domain/schemas/BI";
import { Button, Toolbar } from "@progress/kendo-react-buttons";
import { DATA_ITEM_KEY, EXPANDED_FIELD, SELECTED_FIELD, idGetter } from "@presentation/components/helpers/constants";
import { useTranslation } from "react-i18next";
import { StepsView } from "./components/StepsView";
import StatusCell from "./components/StatusCell";
import { trashIcon, cancelIcon, playIcon, downloadIcon, filterIcon, copyIcon } from "@progress/kendo-svg-icons";
import { DownloadResultsService } from "@application/useCases";
import { useNavigate } from "react-router-dom";
import { SamplesView } from "./components/SamplesView";
import { pauseIcon } from "@presentation/icons/pause";
import { DuplicateJobMetadata, getDuplicateJobMetadataMap } from "@presentation/common";
import { subscribeToJobsList, subscribeToRefreshList } from "@infrastructure/realtime/jobsHubClient";
import { useArtifactDownloadDialog } from "./components/useArtifactDownloadDialog";
import { useBatchArtifactsViewer } from "./components/useBatchArtifactsViewer";

/**
 * Create a context to share with the child/slave components the mutable value of isDropdownFilterFocusedRef in real time.
 */
export const DropdownFilterFocusContext = createContext({
  isDropdownFilterFocusedRef: {
    current: false,
  },
  showFilters: true,
});

export const BatchArtifactsViewerContext = createContext({
  openLogs: async (_jobId: string, _jobPipelineStepId: string, _batch: { id: string; status?: StatusEnumSchema | null }) => {
  },
  openResults: async (_jobId: string, _jobPipelineStepId: string, _batch: { id: string; status?: StatusEnumSchema | null }) => {
  },
});

type BatchArtifactsViewerActions = {
  openLogs: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => Promise<void>;
  openResults: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => Promise<void>;
};

const BatchArtifactsViewerHost: React.FC<{
  listJobsService: ListJobsService;
  actionsRef: React.MutableRefObject<BatchArtifactsViewerActions>;
}> = ({ listJobsService, actionsRef }) => {
  const { artifactsDialog, openLogs, openResults } = useBatchArtifactsViewer(listJobsService);

  actionsRef.current = { openLogs, openResults };

  return <>{artifactsDialog}</>;
};

const findFilterValue = (filter: CompositeFilterDescriptor | FilterDescriptor | undefined, fieldName: string): string | undefined => {
  if (!filter) {
    return undefined;
  }

  if ("filters" in filter) {
    for (const nestedFilter of filter.filters) {
      const value = findFilterValue(nestedFilter as CompositeFilterDescriptor | FilterDescriptor, fieldName);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  return filter.field === fieldName && typeof filter.value === "string" ? filter.value : undefined;
};

const JobList: React.FC = () => {
  const { metaState, setMetaState, jobList, setJobList, setIsExpanded, selectedState, setSelectedState } = useJobListStore((state) => ({
    metaState: state.metaState,
    setMetaState: state.setMetaState,
    jobList: state.jobList,
    setJobList: state.setJobList,
    setIsExpanded: state.setIsExpanded,
    selectedState: state.selectedState,
    setSelectedState: state.setSelectedState,
    setProgress: state.setProgress,
  }));

  const listJobsService = useInjection(ListJobsService);
  const downloadResultsService = useInjection(DownloadResultsService);
  const [selectedJob, setSelectedJob] = useState<JobListItemState | null>(null);
  const [dataResult, setDataResult] = useState<DataResult>();
  const [isDoubleClickDialogVisible, setIsDoubleClickDialogVisible] = useState(false);
  const [selectedSampleBatchId, setSelectedSampleBatchId] = useState<string | null>(null);
  const [selectedSampleJobName, setSelectedSampleJobName] = useState<string | null>(null);
  const { t: loc } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [duplicateJobMetadataMap, setDuplicateJobMetadataMap] = useState<Record<string, DuplicateJobMetadata>>({});
  const navigate = useNavigate();
  const batchArtifactsActionsRef = useRef<BatchArtifactsViewerActions>({
    openLogs: async () => {
    },
    openResults: async () => {
    },
  });
  const triggerDownload = (url: string) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 5000);
  };

  const triggerDownloads = (downloadUrl: string) => {
    downloadUrl
      .split(",")
      .map((url) => url.trim())
      .filter((url) => url)
      .forEach((url, index) => {
        setTimeout(() => {
          triggerDownload(url);
        }, index * 300);
      });
  };
  const { artifactDownloadDialog, openJobDownloadDialog } = useArtifactDownloadDialog({ downloadResultsService, triggerDownloads });

  /**
   * Ref value used to determine if any dropdown filter is focused since refreshign the parent component does make it loose the focus and since we refresh the data every 5 secods until we use signalR,
   *  this can be very annoying for the users. So when this value is true, the refresh is disabled until it turns false again.
   */
  const isDropdownFilterFocusedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const contextValue = React.useMemo(
    () => ({
      isDropdownFilterFocusedRef,
      showFilters,
    }),
    [showFilters],
  );

  const batchArtifactsContextValue = React.useMemo(
    () => ({
      openLogs: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => batchArtifactsActionsRef.current.openLogs(jobId, jobPipelineStepId, batch),
      openResults: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => batchArtifactsActionsRef.current.openResults(jobId, jobPipelineStepId, batch),
    }),
    [],
  );

  const handleDropdownToggle = (isFocused: boolean) => {
    // Set the current value of the ref to the new focus state
    isDropdownFilterFocusedRef.current = isFocused;
  };

  const fetchStepType = useCallback(
    async (jobPipelineStepId: string): Promise<string> => {
      try {
        const stepDetails = await listJobsService.getStepByIDAsync(jobPipelineStepId);
        return stepDetails?.type || "";
      } catch (error) {
        console.error(`Error fetching step type for ${jobPipelineStepId}:`, error);
        return "";
      }
    },
    [listJobsService],
  );

  const fetchJobs = useCallback(async () => {
    // Only fetch jobs if no dropdown filter is focused and not already fetching
    if (isDropdownFilterFocusedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    try {
      const take = metaState.take || 20;
      const skip = metaState.skip || 0;

      // Kendo -> API conversion
      const pageNumber = Math.floor(skip / take) + 1;

      const sort = metaState.sort?.[0];
      const sortDirection = sort?.dir === "asc" ? "Ascending" : sort?.dir === "desc" ? "Descending" : undefined;

      // Look for the filter applied to the "name" column
      const jobNameFilterValue = findFilterValue(metaState.filter as CompositeFilterDescriptor | undefined, "name");
      const jobCreatedByFilterValue = findFilterValue(metaState.filter as CompositeFilterDescriptor | undefined, "createdBy");

      const pagedJobListResponse = await listJobsService.getJobListAsync(pageNumber, take, metaState.sort?.[0]?.field, sortDirection, jobNameFilterValue, jobCreatedByFilterValue);
      const jobListResponse = pagedJobListResponse.list || [];

      const updatedJobList = await Promise.all(
        jobListResponse.map(async (job) => {
          const virtualCreatedAt = job.createdAt ? new Date(job.createdAt) : job.createdAt;
          const virtualStartedAt = job.startedAt ? new Date(job.startedAt) : job.startedAt;
          const virtualFinishedAt = job.finishedAt ? new Date(job.finishedAt) : job.finishedAt;
          let currentStep = "";

          if (job.currentStep && (job.status === StatusEnumSchema.Running || job.status === StatusEnumSchema.Pending)) {
            // Get latest jobList from store to avoid stale closure
            const currentJobList = useJobListStore.getState().jobList;
            // Find matching job in the current list
            const prevJob = currentJobList.find((prevJob) => prevJob.id === job.id);

            // Fetch step type when:
            // 1. We don't have a previous job
            // 2. The step ID has changed (job.currentStep vs prevJob.currentStepId)
            // 3. Or we don't have a currentStep name from before
            const prevStepId = prevJob?.currentStepId;
            if (!prevJob || job.currentStep !== prevStepId || !prevJob.currentStep) {
              const stepType = await fetchStepType(job.currentStep.toString());
              currentStep = formatStepName(stepType);
            } else {
              // Use the previously fetched step type
              currentStep = prevJob.currentStep;
            }
          }

          return {
            ...job,
            virtualCreatedAt,
            virtualStartedAt,
            virtualFinishedAt,
            currentStep,
            currentStepId: job.currentStep, // Store the ID to detect changes next poll
            progress: job.progress || 0,
          };
        }),
      );

      setJobList(updatedJobList);
      setLoading(false);
      setTotalJobs(pagedJobListResponse.totalCount || 0);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setLoading(false);
    } finally {
      isFetchingRef.current = false;
    }
  }, [isDropdownFilterFocusedRef, listJobsService, metaState]);

  // Initial load and manual refresh (on metaState change)
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Keep a ref to fetchJobs so the SignalR effect doesn't depend on it
  // and doesn't reconnect every time fetchJobs changes.
  const fetchJobsRef = useRef(fetchJobs);
  useEffect(() => {
    fetchJobsRef.current = fetchJobs;
  }, [fetchJobs]);

  useEffect(() => {
    const unsubscribeFromJobsList = subscribeToJobsList();
    const unsubscribeFromRefreshList = subscribeToRefreshList(() => {
      fetchJobsRef.current();
    });

    return () => {
      unsubscribeFromRefreshList();
      unsubscribeFromJobsList();
    };
  }, []);

  useEffect(() => {
    setDuplicateJobMetadataMap(getDuplicateJobMetadataMap());
  }, [jobList]);

  useEffect(() => {
    const dataWithSelection = jobList.map((item) => ({
      ...item,
      [SELECTED_FIELD]: !!selectedState[idGetter(item)],
    }));

    const processed = process(dataWithSelection, {
      filter: metaState.filter,
    });

    setDataResult({
      data: processed.data,
      total: totalJobs,
    });
  }, [jobList, selectedState, totalJobs]);

  const handleDataStateChange = (e: GridDataStateChangeEvent) => {
    const { dataState } = e;
    const filterChanged = JSON.stringify(metaState.filter ?? null) !== JSON.stringify(dataState.filter ?? null);

    setMetaState({
      ...metaState,
      skip: filterChanged ? 0 : (dataState.skip ?? 0),
      take: dataState.take ?? 20,
      sort: dataState.sort,
      filter: dataState.filter,
    });
  };

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedState(newSelectedState);
  };

  const handleKeyDown = (event: GridKeyDownEvent) => {
    const newSelectedState = getSelectedStateFromKeyDown({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedState(newSelectedState);
  };

  const closeDialog = () => {
    setIsDoubleClickDialogVisible(false);
  };

  const openJobDetails = (job: JobListItemState) => {
    setSelectedJob(job);
    setIsDoubleClickDialogVisible(true);
  };

  const handleCellClick = (props: GridCustomCellProps, event: React.MouseEvent<HTMLTableCellElement>) => {
    if (props.tdProps?.onClick) {
      props.tdProps.onClick(event);
    } else {
      props.selectionChange?.({ syntheticEvent: event });
    }

    if (event.detail === 2) {
      openJobDetails(props.dataItem);
    }
  };

  const handleExpandChange = async (event: GridExpandChangeEvent) => {
    event.dataItem.expanded = event.value;
    const expandedJob: JobListItemState = event.dataItem;

    if (!expandedJob.id) return;
    setIsExpanded(expandedJob.id, event.value);
  };

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>) => {
    return React.cloneElement(trElement, {
      ...trElement.props,
      title: loc("doubleClickForDetails"),
    });
  };

  const nameHeaderCell = (props: any) => (
    <th className={props.className} style={props.style} colSpan={props.colSpan} rowSpan={props.rowSpan} role={props.role} aria-sort={props["aria-sort"]}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <span>{props.title}</span>
        <Button
          svgIcon={filterIcon}
          size="small"
          fillMode="flat"
          themeColor="secondary"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setShowFilters((prev) => !prev);
          }}
          title="Toggle Filter Row"
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>
    </th>
  );

  if (loading && jobList.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <Loader size="large" type="infinite-spinner" />
      </div>
    );
  }

  const handleDownload = async (dataItem: any) => {
    try {
      await openJobDownloadDialog(dataItem.id);
    } catch (error) {
      alert(`Failed to download job results.`);
    }
  };

  const handleSamplesClick = async (job: JobListItemState) => {
    try {
      const steps = await listJobsService.getJobStepListAsync(job.id);
      const candidateStep = steps.find((step) => !step.isParallel && (step.batchesCount ?? 0) === 1);

      if (!candidateStep) {
        alert("No single-batch step samples are available for this job.");
        return;
      }

      const batchResponse = await listJobsService.getJobStepBatchListAsync(job.id, candidateStep.id);
      const batchId = batchResponse.list?.[0]?.id;
      if (!batchId) {
        alert("No batch found for the selected step.");
        return;
      }

      setSelectedSampleBatchId(batchId);
      setSelectedSampleJobName(job.name);
    } catch (error) {
      console.error("Error loading samples:", error);
    }
  };

  const closeSamplesDialog = () => {
    setSelectedSampleBatchId(null);
    setSelectedSampleJobName(null);
  };

  const openDuplicateJobSetup = (jobId: string) => {
    navigate(`/job-setup?rerunJobId=${jobId}`);
  };

  const getDuplicateMetadata = (jobId: string) => duplicateJobMetadataMap[jobId] ?? null;

  const getDuplicateSummary = (metadata: DuplicateJobMetadata | null) => {
    if (!metadata) return null;

    return {
      sourceLabel: metadata.sourceJobName
    };
  };

  const selectedJobIds = Object.keys(selectedState).filter((id) => Boolean(selectedState[id]));
  const hasSelectedJobs = selectedJobIds.length > 0;
  const selectedDuplicableJobs = selectedJobIds
    .map((jobId) => jobList.find((job) => job.id === jobId))
    .filter((job): job is JobListItemState => !!job);

  return (
    <div className="fill-all" style={{ overflow: "hidden" }}>
      {artifactDownloadDialog}
      <Toolbar className="page-toolbar" style={{ border: "none", background: "transparent", marginLeft : "-14px" }}>
        <Button
          svgIcon={pauseIcon}
          themeColor="warning"
          title="Pause selected jobs"
          disabled={!hasSelectedJobs}
          onClick={async () => {
            const confirmed = window.confirm("Are you sure you want to pause the selected jobs? This will stop non-running batches while preserving completed and recoverable progress.");
            if (!confirmed) return;

            try {
              await Promise.all(selectedJobIds.map((jobId) => listJobsService.cancelPendingBatchesByJobIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error pausing jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={playIcon}
          themeColor="info"
          title="Resume selected jobs"
          disabled={!hasSelectedJobs}
          style={{ marginLeft: "-2px" }}
          onClick={async () => {
            const confirmed = window.confirm("Are you sure you want to resume the selected jobs? Only paused, aborted or failed jobs can be resumed.");
            if (!confirmed) return;

            try {
              await Promise.all(selectedJobIds.map((jobId) => listJobsService.resumeJobByIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error resuming jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={cancelIcon}
          themeColor="error"
          title="Abort selected jobs"
          disabled={!hasSelectedJobs}
          style={{ marginLeft: "-2px" }}
          onClick={async () => {
            const confirmed = window.confirm("Are you sure you want to abort the selected jobs? This will stop the execution of all their active batches(Scheduled, Pending or Running).");
            if (!confirmed) return;

            try {
              await Promise.all(selectedJobIds.map((jobId) => listJobsService.abortJobByIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error aborting jobs:", error);
            }
          }}
        />
        <Button
          svgIcon={copyIcon}
          title="Duplicate selected job"
          disabled={selectedDuplicableJobs.length !== 1}
          style={{ marginLeft: "-2px" }}
          onClick={() => openDuplicateJobSetup(selectedDuplicableJobs[0].id)}
        />
        <Button
          svgIcon={trashIcon}
          title="Delete selected jobs"
          disabled={!hasSelectedJobs}
          style={{ marginLeft: "-2px" }}
          onClick={async () => {
            const confirmed = window.confirm("Are you sure you want to delete the selected jobs? This will also remove the folder containing the results.");
            if (!confirmed) return;

            const finishedJobIds = selectedJobIds.filter((jobId) => {
              const job = jobList.find((job) => job.id === jobId);
              return job?.status === StatusEnumSchema.Completed || job?.status === StatusEnumSchema.Failed || job?.status === StatusEnumSchema.Aborted || job?.status === StatusEnumSchema.Paused;
            });

            if (finishedJobIds.length < selectedJobIds.length) {
              alert("You can only delete not active jobs. Some selected jobs are still active and will not be deleted. To delete them you must abort them first or let them finish");
            }

            try {
              await Promise.all(finishedJobIds.map((jobId) => listJobsService.deleteJobByIDAsync(jobId)));
              fetchJobs();
            } catch (error) {
              console.error("Error deleting jobs:", error);
            }
          }}
        />

      </Toolbar>
      <BatchArtifactsViewerContext.Provider value={batchArtifactsContextValue}>
        <BatchArtifactsViewerHost listJobsService={listJobsService} actionsRef={batchArtifactsActionsRef} />
        <DropdownFilterFocusContext.Provider value={contextValue}>
          <Grid
            className="page-content-with-toolbar"
            resizable={true}
            dataItemKey={DATA_ITEM_KEY}
            selectedField={SELECTED_FIELD}
            expandField={EXPANDED_FIELD}
            scrollable="scrollable"
            navigatable={true}
            filterable={showFilters}
            sortable={true}
            pageable={{
              buttonCount: 5,
              pageSizes: [1, 10, 20, 50, 100],
              previousNext: true,
              info: true,
            }}
            skip={metaState.skip || 0}
            take={metaState.take || 20}
            selectable={{
              enabled: true,
              drag: false,
              mode: "multiple",
            }}
            {...metaState}
            data={dataResult}
            detail={StepsView}
            onExpandChange={handleExpandChange}
            onDataStateChange={handleDataStateChange}
            onSelectionChange={handleSelectionChange}
            onKeyDown={handleKeyDown}
            rowRender={rowRender}
            onRowClick={(event: GridRowClickEvent) => {
              if ((event.syntheticEvent as React.MouseEvent<HTMLElement>).detail === 2) {
                openJobDetails(event.dataItem);
              }
            }}
          >
          <GridColumn
            field="name"
            title="Name"
            resizable={true}
            minResizableWidth={300}
            headerCell={nameHeaderCell}
            cell={(props: GridCustomCellProps) => {
              const metadata = getDuplicateMetadata(props.dataItem.id);
              const summary = getDuplicateSummary(metadata);

              return (
                <td
                  {...props.tdProps}
                  onClick={(event) => {
                    handleCellClick(props, event);
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <span>{props.dataItem.name}</span>
                    {summary && (
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                        <span style={{ fontSize: "12px", color: "#666" }} title={`Source: ${summary.sourceLabel}`}>
                          Duplicated from {summary.sourceLabel}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
              );
            }}
          />
          <GridColumn
            field="status"
            title={loc("status")}
            minResizableWidth={200}
            cell={(props: GridCustomCellProps) => (
              <StatusCell
                status={props.dataItem.status}
                isPauseRequested={Boolean(props.dataItem.pendingResumeFromStepId)}
                progress={props.dataItem.progress}
                isbatch={false}
                currentStep={props.dataItem.currentStep}
                failedBatchesCount={props.dataItem.failedBatchesCount}
                tdProps={props.tdProps ?? undefined}
                selectionChange={props.selectionChange}
              />
            )}
            filterCell={(props) => <DropdownFilterCell {...props} data={Object.values(StatusEnumSchema) as string[]} defaultItem={"Select status"} onToggle={handleDropdownToggle} />}
          />

          <GridColumn
            field="pipelineType"
            title="Pipeline"
            resizable={true}
            minResizableWidth={130}
            cell={(props: GridCustomCellProps) => (
              <td
                {...props.tdProps}
                onClick={(event) => {
                  handleCellClick(props, event);
                }}
              >
                {formatStepName(props.dataItem.pipelineType ?? "")}
              </td>
            )}
            filterCell={(props) => (
              <DropdownFilterCell {...props} data={Object.values(JobPipelineTypeSchema).map((v) => formatStepName(v as string))} defaultItem={"Select Pipeline"} onToggle={handleDropdownToggle} />
            )}
          />
          <GridColumn
            field="softwareVersion"
            title={loc("softwareVersion")}
            resizable={true}
            minResizableWidth={150}
            filter="text"
            cell={(props: GridCustomCellProps) => (
              <td
                {...props.tdProps}
                onClick={(event) => {
                  handleCellClick(props, event);
                }}
              >
                {props.dataItem.softwareVersion ?? ""}
              </td>
            )}
          />
          <GridColumn
            field="createdBy"
            title={loc("createdBy")}
            minResizableWidth={100}
            width={200}
            cell={(props: GridCustomCellProps) => (
              <td
                {...props.tdProps}
                onClick={(event) => {
                  handleCellClick(props, event);
                }}
              >
                {props.dataItem.createdBy ?? ""}
              </td>
            )}
          />
          <GridColumn
            title="Avg. Protein Groups"
            resizable={true}
            minResizableWidth={80}
            width={150}
            cell={(props: GridCustomCellProps) => {
              const description = props.dataItem.description || "";
              const proteinGroupsMatch = description.match(/Avg Profiles - Protein Groups\s*[\s\S]*?(\d{1,3}(?:,\d{3})*|\d+)/i);
              const proteinGroupsCount = proteinGroupsMatch ? proteinGroupsMatch[1] : "N/A";

              return (
                <td
                  {...props.tdProps}
                  title={description}
                  style={{ ...(props.tdProps?.style ?? {}), cursor: description ? "help" : "default" }}
                  onClick={(event) => {
                    handleCellClick(props, event);
                  }}
                >
                  {proteinGroupsCount}
                </td>
              );
            }}
          />
          <GridColumn
            title={"Results"}
            width="200%"
            minResizableWidth={220}
            cell={(props: GridCustomCellProps) => (
              <td
                {...props.tdProps}
                style={{ ...(props.tdProps?.style ?? {}), textAlign: "left" }}
                onClick={(event) => {
                  handleCellClick(props, event);
                }}
              >
                {props.dataItem.status === StatusEnumSchema.Completed && (
                  <a
                    href="#"
                    {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownload(props.dataItem);
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <Button themeColor="secondary" fillMode="link" style={{ padding: 0 }} svgIcon={downloadIcon}>
                      {props.dataItem.precursorsCount != null ? props.dataItem.precursorsCount : "Download"}
                    </Button>
                  </a>
                )}
              </td>
            )}
          />
          <GridColumn
            title={"Samples"}
            width="150px"
            minResizableWidth={130}
            cell={(props: GridCustomCellProps) => (
              <td
                {...props.tdProps}
                style={{ ...(props.tdProps?.style ?? {}), textAlign: "left" }}
                onClick={(event) => {
                  handleCellClick(props, event);
                }}
              >
                <Button
                  {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}
                  themeColor="secondary"
                  fillMode="link"
                  style={{ padding: 0 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSamplesClick(props.dataItem);
                  }}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  View Samples
                </Button>
              </td>
            )}
          />
          {/* <GridColumn field="id" title="ID" minResizableWidth={150} /> */}
          </Grid>
          {/* expand advanced job details on double click  */}
          {isDoubleClickDialogVisible && selectedJob && (
            <Dialog title={`${selectedJob.name} - Job Details`} onClose={closeDialog} width={800}>
              <div style={{ padding: "10px", maxHeight: "80vh", overflowY: "auto" }}>
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" }}>
                      <div>
                        <strong>Job ID:</strong> {selectedJob.id}
                      </div>
                      {selectedJob.createdAt && (
                        <div>
                          <strong>Created At:</strong> {new Date(selectedJob.createdAt).toLocaleString()}
                        </div>
                      )}
                      {selectedJob.finishedAt && (
                        <div>
                          <strong>Finished At:</strong> {new Date(selectedJob.finishedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {getDuplicateMetadata(selectedJob.id) && (() => {
                    const metadata = getDuplicateMetadata(selectedJob.id)!;

                    return (
                      <>
                        <h3>Duplicated From</h3>
                        <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                          <div><strong>Job:</strong> {metadata.sourceJobName}</div>
                          {metadata.sourceSoftwareVersion && <div><strong>Spectronaut Version:</strong> {metadata.sourceSoftwareVersion}</div>}
                        </div>

                      </>
                    );
                  })()}

              {selectedJob.softwareSettingsFile && (
                <>
                  <h3>Software Settings File</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                      {typeof selectedJob.softwareSettingsFile === "string" ? selectedJob.softwareSettingsFile.replace(/^fasta\//, "") : JSON.stringify(selectedJob.softwareSettingsFile, null, 2)}
                    </pre>
                  </div>
                </>
              )}

              {selectedJob.conditionSetupFile && (
                <>
                  <h3>Condition Setup</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{selectedJob.conditionSetupFile.replace(/^fasta\//, "")}</pre>
                  </div>
                </>
              )}

              {selectedJob.reportSettingsFile &&
                ((typeof selectedJob.reportSettingsFile === "string" && selectedJob.reportSettingsFile !== "") ||
                  (Array.isArray(selectedJob.reportSettingsFile) && selectedJob.reportSettingsFile.length > 0)) && (
                  <>
                    <h3>Report Settings</h3>
                    <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                      <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                        {Array.isArray(selectedJob.reportSettingsFile)
                          ? selectedJob.reportSettingsFile.map((file) => file.replace(/^fasta\//, "")).join("\n")
                          : typeof selectedJob.reportSettingsFile === "string"
                            ? (selectedJob.reportSettingsFile as string).replace(/^fasta\//, "")
                            : ""}
                      </pre>
                    </div>
                  </>
                )}

              {selectedJob.fastaFiles && selectedJob.fastaFiles.length > 0 && (
                <>
                  <h3>Fasta Files</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                      {Array.isArray(selectedJob.fastaFiles)
                        ? selectedJob.fastaFiles.map((file) => file.replace(/^fasta\//, "")).join("\n")
                        : typeof selectedJob.fastaFiles === "string"
                          ? (selectedJob.fastaFiles as string).replace(/^fasta\//, "")
                          : ""}
                    </pre>
                  </div>
                </>
              )}
              {selectedJob.libraryFiles && selectedJob.libraryFiles.length > 0 && (
                <>
                  <h3>Library Files</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                      {Array.isArray(selectedJob.libraryFiles)
                        ? selectedJob.libraryFiles.map((file) => file.replace(/^library\//, "")).join("\n")
                        : typeof selectedJob.libraryFiles === "string"
                          ? (selectedJob.libraryFiles as string).replace(/^library\//, "")
                          : ""}
                    </pre>
                  </div>
                </>
              )}

              {selectedJob.archiveFiles && selectedJob.archiveFiles.length > 0 && (
                <>
                  <h3>Archive Files</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                      {Array.isArray(selectedJob.archiveFiles) ? selectedJob.archiveFiles.join("\n") : typeof selectedJob.archiveFiles === "string" ? (selectedJob.archiveFiles as string) : ""}
                    </pre>
                  </div>
                </>
              )}

              {selectedJob.goFile && selectedJob.goFile.length > 0 && (
                <>
                  <h3>Gene Annotation Files</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>
                      {Array.isArray(selectedJob.goFile) ? selectedJob.goFile.join("\n") : typeof selectedJob.goFile === "string" ? (selectedJob.goFile as string) : ""}
                    </pre>
                  </div>
                </>
              )}

              {selectedJob.description && (
                <>
                  <h3>Results overview</h3>
                  <div style={{ backgroundColor: "#f5f5f5", padding: "10px", borderRadius: "3px", marginBottom: "10px" }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0, fontSize: "14px" }}>{selectedJob.description}</pre>
                  </div>
                </>
              )}

                  {selectedJob && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                      <Button
                        themeColor="primary"
                        onClick={() => {
                          closeDialog();
                          openDuplicateJobSetup(selectedJob.id);
                        }}
                      >
                        Duplicate Job And Run
                      </Button>
                    </div>
                  )}
              </div>
            </Dialog>
          )}
          {selectedSampleBatchId && (
            <Dialog
              title={`${selectedSampleJobName ?? "Job"} samples`}
              onClose={closeSamplesDialog}
              width={"min(1200px, calc(100vw - 32px))"}
              height={"min(85vh, 900px)"}
            >
              <SamplesView dataItem={{ id: selectedSampleBatchId }} dataIndex={0} />
            </Dialog>
          )}
        </DropdownFilterFocusContext.Provider>
      </BatchArtifactsViewerContext.Provider>
    </div>
  );
};

export default JobList;

export function formatStepName(stepName: string | null | undefined): string {
  if (!stepName) return "";
  
  // Keep "directDIA" as-is
  if (stepName === "DirectDIA") return "directDIA";

  return stepName
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between camelCase/PascalCase
    .replace(/([a-z])(\d)/g, "$1 $2") // Add space between letters and numbers
    .replace(/(\d)([a-z])/g, "$1 $2") // Add space between numbers and letters
    .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
    .replace(/\b(Dia|Sne)\b/g, (acronym) => acronym.toUpperCase()); // Ensure "DIA" and "SNE" remain fully capitalized
}
